
import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Crown, ExternalLink, Award, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

interface FeaturedFounderProps {
  name: string;
  age: number;
  company: string;
  description: string;
  achievement: string;
  avatar?: string;
  socialLink?: string;
  featured?: boolean;
}

const FeaturedFounderCard: React.FC<FeaturedFounderProps> = ({
  name,
  age,
  company,
  description,
  achievement,
  avatar,
  socialLink,
  featured = false
}) => {
  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9, rotateY: -15 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      rotateY: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    },
    hover: {
      scale: 1.03,
      rotateY: 5,
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    }
  };

  const avatarVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: { 
      scale: 1, 
      rotate: 0,
      transition: {
        delay: 0.3,
        duration: 0.5,
        type: "spring",
        stiffness: 200
      }
    }
  };

  const contentVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: {
        delay: 0.4,
        duration: 0.5
      }
    }
  };

  const badgeVariants = {
    hidden: { opacity: 0, scale: 0 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: {
        delay: 0.5,
        duration: 0.3,
        type: "spring"
      }
    }
  };

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover="hover"
      className="relative perspective-1000"
    >
      <Card className={`overflow-hidden border-2 transition-all duration-300 ${
        featured 
          ? 'border-[#FF8C00] shadow-xl shadow-[#FF8C00]/20 bg-gradient-to-br from-[#FF8C00]/5 to-orange-500/5' 
          : 'border-border hover:border-[#FF8C00]/50'
      }`}>
        {featured && (
          <motion.div 
            className="absolute -top-2 -left-2 z-10"
            variants={badgeVariants}
            initial="hidden"
            animate="visible"
          >
            <Badge className="bg-gradient-to-r from-[#FF8C00] to-orange-500 text-white px-3 py-1 shadow-lg">
              <Crown className="h-3 w-3 mr-1" />
              Featured Founder
            </Badge>
          </motion.div>
        )}

        <CardHeader className="text-center pb-4">
          <motion.div variants={avatarVariants} className="flex justify-center">
            <Avatar className={`h-20 w-20 border-4 ${featured ? 'border-[#FF8C00]' : 'border-muted'}`}>
              <AvatarImage src={avatar} alt={name} />
              <AvatarFallback className="text-lg font-bold">
                {name.split(' ').map(n => n[0]).join('')}
              </AvatarFallback>
            </Avatar>
          </motion.div>
          
          <motion.div variants={contentVariants} className="space-y-2">
            <h3 className="text-xl font-bold flex items-center justify-center gap-2">
              {name}
              {featured && <Sparkles className="h-4 w-4 text-[#FF8C00]" />}
            </h3>
            <div className="flex justify-center gap-2">
              <Badge variant="secondary">{age} years old</Badge>
              <Badge variant="outline">{company}</Badge>
            </div>
          </motion.div>
        </CardHeader>

        <CardContent className="pt-0">
          <motion.div variants={contentVariants} className="space-y-4">
            <p className="text-muted-foreground text-sm text-center">
              {description}
            </p>
            
            <motion.div 
              className="bg-muted/50 rounded-lg p-3"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex items-center gap-2 mb-2">
                <Award className="h-4 w-4 text-[#FF8C00]" />
                <span className="text-sm font-medium">Key Achievement</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {achievement}
              </p>
            </motion.div>
            
            {socialLink && (
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex justify-center"
              >
                <Button 
                  size="sm" 
                  variant={featured ? "default" : "outline"}
                  onClick={() => window.open(socialLink, '_blank')}
                  className={`flex items-center gap-2 ${
                    featured ? 'bg-[#FF8C00] hover:bg-orange-600' : ''
                  }`}
                >
                  <ExternalLink className="h-3 w-3" />
                  Connect
                </Button>
              </motion.div>
            )}
          </motion.div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default FeaturedFounderCard;
